import { Container, Typography, CircularProgress, Box, Skeleton, Stack, Grid, Paper, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { MUSIC_QUOTES } from '../constants/musicQuotes';
import { getTitoEvents, getTitoRegistrations, getTitoReleases } from '../services/titoApi';
import { Event, Performance, Application, TitoEvent } from '../types/dashboard';
import axiosInstance from '../utils/axiosConfig';
import UserGreeting from '../components/UserGreeting';
import QuoteCard from '../components/dashboard/QuoteCard';
import UserTasksOverview from '../components/dashboard/UserTasksOverview';
import { useAuth } from '../contexts/AuthContext';
import { Suspense, useEffect, useState } from 'react';
import React from 'react';
import WhosWorkingToday from '../components/WhosWorkingToday';
import RotaWidget from '../components/RotaWidget';
import NextGig from '../components/NextGig';
import PendingBandsWidget from '../components/dashboard/PendingBandsWidget';

// Lazy load larger components
const DashboardStatsCards = React.lazy(() => import('../components/dashboard/DashboardStatsCards'));
const DashboardTabs = React.lazy(() => import('../components/dashboard/DashboardTabs'));
const SalesAnalysisSection = React.lazy(() => import('../components/dashboard/SalesAnalysisSection'));

interface DashboardData {
  upcomingPerformances: Performance[];
  performances: Performance[];
  titoEvents: TitoEvent[];
  pendingApplications: number;
  totalApplications: number;
  totalBands: number;
  lastLogin: string | null;
  ticketsSoldSinceLastLogin: number;
}

// Initial loading component
const LoadingFallback = () => (
  <Box 
    sx={{ 
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      pt: 4
    }}
  >
    <CircularProgress />
    <Typography variant="h6" color="text.secondary">
      Loading Dashboard...
    </Typography>
  </Box>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'gm';
  const isAdminLevel = ['admin', 'superadmin', 'gm'].includes(user?.role ?? '');
  const isTeam = user?.role === 'team';
  const navigate = useNavigate();
  
  // All state variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [currentQuote] = useState(() => 
    MUSIC_QUOTES[Math.floor(Math.random() * MUSIC_QUOTES.length)]
  );
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    upcomingPerformances: [],
    performances: [],
    titoEvents: [],
    pendingApplications: 0,
    totalApplications: 0,
    totalBands: 0,
    lastLogin: null,
    ticketsSoldSinceLastLogin: 0
  });

  console.log('Dashboard - Current user:', {
    exists: !!user,
    id: user?._id,
    role: user?.role,
    isAdmin,
    isTeam
  });

  const handleRosterClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // Don't navigate if clicking on interactive elements
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('[data-interactive]')) {
      return;
    }
    
    navigate('/admin/rota?scroll=calendar');
  };

  // Primary data fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch both programmings and performances data
        const apiCalls = [
          axiosInstance.get<any[]>('/api/programmings'), // Changed from /api/events
          axiosInstance.get<Performance[]>('/api/performances'),
          axiosInstance.get<any[]>('/api/bandrecommendations')
        ];

        if (isAdmin) {
          apiCalls.push(
            axiosInstance.get<any[]>('/api/applications/submissions')
          );
        }

        const responses = await Promise.all(apiCalls);
        
        // Process programmings to create "virtual" performance data
        const programmings = responses[0].data;
        const actualPerformances = responses[1].data;
        
        const pendingFromProgrammings = programmings
          .filter(prog => prog.hasPerformance === false && prog.status === 'BOOKED')
          .map(prog => ({
            _id: prog._id,
            // Use the actual artist name from programming
            artistName: prog.artistName,
            band: {
              artistName: prog.artistName,
              genre: prog.genre,
              numPerformers: prog.numPerformers,
              expectedDraw: prog.expectedDraw
            },
            date: prog.date,
            status: 'pending',
            venue: null, // Programming doesn't have venue assigned yet
            startTime: prog.date,
            endTime: prog.date,
            // Add additional programming details for display
            primaryContact: prog.primaryContact,
            email: prog.email,
            phone: prog.phone,
            pricePoint: prog.pricePoint,
            bookingSource: prog.bookingSource,
            notes: prog.notes,
            source: 'programming', // Flag to identify source
            programmingData: prog // Include full programming data
          }));

        const confirmedPerformances = actualPerformances.map(perf => ({
          ...perf,
          status: 'confirmed',
          source: 'performance'
        }));

        // Combine both types
const performanceArtistDates = new Set(
  confirmedPerformances.map(p => {
    const artist = p.simpleBandName || p.band?.artistName || '';
    const start = p.startTime ? new Date(p.startTime).toDateString() : '';
    return `${artist}-${start}`;
  })
);

const filteredProgrammings = pendingFromProgrammings.filter(prog => {
  const artist = prog.artistName || prog.band?.artistName || '';
  const start = prog.startTime ? new Date(prog.startTime).toDateString() : '';
  return !performanceArtistDates.has(`${artist}-${start}`);
});

const allPerformances = [...confirmedPerformances, ...filteredProgrammings];

let newData: Partial<DashboardData> = {
  upcomingPerformances: allPerformances,
  performances: allPerformances,
  totalBands: responses[2].data.length
};

if (isAdmin && responses.length > 3) {
  // Get all application submissions
  const allApplications = responses[3].data;
  // Filter pending applications only
  newData.pendingApplications = allApplications.filter((app: any) => app.status === 'pending').length;
  newData.totalApplications = allApplications.length;
}

setDashboardData(prev => ({
  ...prev,
  ...newData
}));

setLoading(false);
      } catch (err) {
        console.error('Error fetching initial dashboard data:', err);
        setError('Failed to fetch dashboard data');
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [isAdmin]);

  // Secondary data fetching (Tito data, etc.)
  useEffect(() => {
    if (!loading) {
      const fetchSecondaryData = async () => {
        try {
          console.log('Fetching secondary data...');
          const [userResponse, titoResponse] = await Promise.all([
            axiosInstance.get<{ lastLogin: string }>('/api/users/me/lastLogin'),
            getTitoEvents()
          ]);

          console.log('Tito response:', titoResponse);

          if (titoResponse.data?.events) {
            console.log('Processing Tito events:', titoResponse.data.events);
            const processedEvents = await processTicketData(titoResponse.data.events);
            console.log('Processed events:', processedEvents);
            
            setDashboardData(prev => {
              console.log('Updating dashboard data with events:', processedEvents);
              return {
                ...prev,
                titoEvents: processedEvents,
                lastLogin: userResponse.data.lastLogin
              };
            });
          } else {
            console.warn('No events found in Tito response');
          }
        } catch (err) {
          console.error('Error fetching secondary data:', err);
        }
      };

      fetchSecondaryData();
    }
  }, [loading]);

  // Helper function to process Tito events data
  const processTicketData = async (events: any[]): Promise<TitoEvent[]> => {
    console.log('Starting to process ticket data for events:', events);

    try {
      const processedEvents = await Promise.all(
        events
          .filter(event => {
            console.log('Checking event:', event.title, 'live status:', event.live);
            return event.live;
          })
          .map(async (event) => {
            console.log('Fetching details for event:', event.title);
            try {
              const [registrationsResponse, releasesResponse] = await Promise.all([
                getTitoRegistrations(event.slug),
                getTitoReleases(event.slug)
              ]);

              console.log('Registrations for', event.title, ':', registrationsResponse.data);
              console.log('Releases for', event.title, ':', releasesResponse.data);

              const totalSold = registrationsResponse.data.registrations.reduce(
                (sum: number, reg: any) => sum + reg.tickets_count,
                0
              );

              const totalAvailable = releasesResponse.data.releases.reduce(
                (sum: number, release: any) => sum + release.quantity,
                0
              );

              return {
                title: event.title,
                totalSold,
                totalAvailable,
                start_date: event.start_date,
                releases: releasesResponse.data.releases,
                registrations: registrationsResponse.data.registrations,
                last_ticket_sold_at: registrationsResponse.data.registrations[0]?.completed_at
              };
            } catch (error) {
              console.error('Error processing event:', event.title, error);
              return null;
            }
          })
      );

      console.log('Processed events before filtering:', processedEvents);
      const filteredEvents = processedEvents
        .filter((event): event is TitoEvent => {
          if (event === null) return false;
          
          const today = new Date().toISOString().split('T')[0]; // "2025-06-12"
          const eventDate = event.start_date.split('T')[0]; // "2025-06-12" 
          
          return eventDate >= today;
        });
      console.log('Final filtered events:', filteredEvents);
      return filteredEvents;
    } catch (error) {
      console.error('Error in processTicketData:', error);
      return [];
    }
  };

  // Early returns after all hooks
  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  // Calculate derived data
  const pendingPerformances = dashboardData.performances.filter(p => p.status === 'pending');
  const confirmedPerformances = dashboardData.performances.filter(p => p.status === 'confirmed');
  
  // Get next performance from existing data - NO API CALL NEEDED!
const getNextPerformance = () => {
  const now = new Date();
  return dashboardData.performances
    .filter(p => 
      new Date(p.startTime) > now && 
      p.startTime &&
      p.source === 'performance'
    )
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0] || null;
};
  
  const nextPerformance = getNextPerformance();
    
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        {isTeam ? 'Team Dashboard' : 'Your Dashboard'}
      </Typography>
      
      {/* Show NextGig only if we have a valid next performance */}
      {nextPerformance && nextPerformance.startTime && (
        <Box sx={{ mb: 4 }}>
          <NextGig performance={nextPerformance} />
        </Box>
      )}

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%' }}>
            <UserGreeting ticketsSold={dashboardData.ticketsSoldSinceLastLogin} />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%' }}>
            <QuoteCard quote={currentQuote} />
          </Box>
        </Grid>
      </Grid>

      {isAdminLevel && <Box sx={{ my: 3 }}><PendingBandsWidget /></Box>}

      <RotaWidget />

      <Grid item xs={12} md={12}>
        <Box 
          onClick={handleRosterClick}
          sx={{ 
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.01)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <WhosWorkingToday />
        </Box>
      </Grid>

      {/* Team Quick Access Card */}
      {isTeam && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {/* Programming Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Programming</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Manage performances, bands, and events.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button 
                  component={Link} 
                  to="/admin/programming"
                  variant="contained" 
                  color="secondary"
                  sx={{ mr: 2, mb: 2 }}
                >
                  IMC Bookings
                </Button>
                <Button 
                  component={Link} 
                  to="/admin/performances"
                  variant="contained"
                  color='success' 
                  sx={{ mr: 2, mb: 2 }}
                >
                  Confirmed Shows
                </Button>
                <Button 
                  component={Link} 
                  to="/admin/bands"
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 2 }}
                >
                  DB Bands
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* The Cooler Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>The Cooler</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                View and manage The Cooler Venue.
              </Typography>
              <Button 
                component={Link} 
                to="/admin/taskboard"
                variant="contained"
                color="primary" 
                sx={{ mr: 2, mb: 2 }}
              >
                Cooler Tasks
              </Button>
              <Button 
                component={Link} 
                to="/admin/inventory"
                variant="outlined"
                color="primary"
                sx={{ mb: 2 }}
              >
                Cooler Inventory
              </Button>
              <Button 
                component={Link} 
                to="/stage-designer"
                variant="outlined"
                sx={{ mb: 2 }}
              >
                Stage Designer
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tasks Overview */}
      {user?._id && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <UserTasksOverview userId={user._id} />
        </Box>
      )}

      <Suspense fallback={
        <Stack spacing={3} sx={{ mt: 4 }}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={400} />
        </Stack>
      }>
        {/* Stats Cards */}
        <Box sx={{ mt: 4 }}>
          <DashboardStatsCards 
            stats={{
              upcomingPerformances: dashboardData.performances.length,
              totalBands: dashboardData.totalBands,
              pendingPerformances: pendingPerformances.length,
              confirmedPerformances: confirmedPerformances.length,
              pendingApplications: dashboardData.pendingApplications || 0,
              totalApplications: dashboardData.totalApplications || 0
            }}
            onTabChange={setTabValue}
          />
        </Box>

        {/* Sales Analysis - only for admins */}
        {isAdmin && (
          <Box sx={{ mt: 4 }}>
            <SalesAnalysisSection 
              events={dashboardData.titoEvents}
              expanded={expanded}
              onAccordionChange={(panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
                setExpanded(isExpanded ? panel : false);
              }}
            />
          </Box>
        )}
      
      </Suspense>
    </Container>
  );
};

export default Dashboard;